import type { ReactNode } from 'react'
import { PexIcon } from '../../components/icons/PexIcon'
import {
  MobileOperationsHeader,
  type MobileOperationsQueue,
} from '../../components/operations/mobile-operations-header'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { MobileFilterSheet } from '../../components/ui/mobile-filter-sheet'
import type { WorkOrder } from '../../mocks/data/work-orders'
import {
  SupplyMobileWorkOrderList,
  type SupplyMobileWorkOrderListLabels,
} from './SupplyMobileWorkOrderList'

type SupplyMobileWorkOrdersSurfaceLabels = {
  queue: string
  count: (count: number) => string
  search: string
  closeSearch: string
  searchPlaceholder: string
  filters: string
  filterTitle: string
  cancel: string
  apply: string
  clear: string
  refresh: string
}

type SupplyMobileWorkOrdersSurfaceProps<Q extends string> = {
  queue: Q
  queues: ReadonlyArray<MobileOperationsQueue<Q>>
  total: number
  search: string
  searchOpen: boolean
  activeFilterCount: number
  filterOpen: boolean
  refreshing: boolean
  workOrders: WorkOrder[]
  loading: boolean
  error: boolean
  now: number
  labels: SupplyMobileWorkOrdersSurfaceLabels
  listLabels: SupplyMobileWorkOrderListLabels
  filterContent: ReactNode
  onQueueChange: (queue: Q) => void
  onSearchToggle: () => void
  onSearchChange: (value: string) => void
  onFilterOpen: () => void
  onFilterCancel: () => void
  onFilterApply: () => void
  onFilterClear: () => void
  onRefresh: () => void
  onOpenWorkOrder: (id: string) => void
  onRetry: () => void
}

export function SupplyMobileWorkOrdersSurface<Q extends string>({
  queue,
  queues,
  total,
  search,
  searchOpen,
  activeFilterCount,
  filterOpen,
  refreshing,
  workOrders,
  loading,
  error,
  now,
  labels,
  listLabels,
  filterContent,
  onQueueChange,
  onSearchToggle,
  onSearchChange,
  onFilterOpen,
  onFilterCancel,
  onFilterApply,
  onFilterClear,
  onRefresh,
  onOpenWorkOrder,
  onRetry,
}: SupplyMobileWorkOrdersSurfaceProps<Q>) {
  return (
    <>
      <section
        className="md:hidden"
        aria-label={labels.count(total)}
        data-testid="supply-work-orders-mobile-list"
      >
        <MobileOperationsHeader
          queueLabel={labels.queue}
          queue={queue}
          queues={queues}
          total={total}
          totalLabel={labels.count(total)}
          searchOpen={searchOpen}
          searchLabel={labels.search}
          closeSearchLabel={labels.closeSearch}
          filterLabel={labels.filters}
          activeFilterCount={activeFilterCount}
          onQueueChange={onQueueChange}
          onSearchToggle={onSearchToggle}
          onFilterOpen={onFilterOpen}
          primaryAction={
            <Button
              type="button"
              variant="primary"
              size="icon"
              aria-label={labels.refresh}
              disabled={refreshing}
              onClick={onRefresh}
            >
              <PexIcon
                name="refresh"
                size="sm"
                tone="on-emphasis"
                className={refreshing ? 'animate-spin' : ''}
                decorative
              />
            </Button>
          }
          searchField={
            <div className="relative">
              <PexIcon
                name="search"
                size="sm"
                tone="muted"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                decorative
              />
              <Input
                value={search}
                className="pl-9"
                placeholder={labels.searchPlaceholder}
                aria-label={labels.search}
                onChange={(event) => onSearchChange(event.target.value)}
              />
            </div>
          }
        />

        <SupplyMobileWorkOrderList
          workOrders={workOrders}
          labels={listLabels}
          loading={loading}
          error={error}
          now={now}
          onOpen={onOpenWorkOrder}
          onRetry={onRetry}
        />
      </section>

      <MobileFilterSheet
        open={filterOpen}
        title={labels.filterTitle}
        cancelLabel={labels.cancel}
        applyLabel={labels.apply}
        clearLabel={labels.clear}
        onCancel={onFilterCancel}
        onApply={onFilterApply}
        onClear={onFilterClear}
      >
        {filterContent}
      </MobileFilterSheet>
    </>
  )
}
